import { ContractIdResultItem } from './ContractIdResultItem';
import { ModalFooter } from './ModalFooter';
import {
  ErrorMessageStyle,
  ResultsCountLabelStyle,
  ResultsHeaderStyle,
  ResultsListStyle,
  ResultsRowStyle,
  SelectAllRowStyle,
  SelectAllTextStyle,
  SearchResultsStyle,
  CheckboxStyle,
} from '../ContractIdSelectionModal.style';
import type { ContractIdData } from '../types/ContractIdModal.types';

interface SearchResultsProps {
  results: ContractIdData[];
  selectedIds: Set<string>;
  remainingTaxIds: number;
  taxIdLimit: number;
  rangeStart: number;
  rangeEnd: number;
  totalCount: number;
  onToggle: (contractId: string) => void;
  onDeselectAll: () => void;
  hasSelections: boolean;
  selectedCount: number;
  onClose: () => void;
  onApply: () => void;
  tableData: ContractIdData[];
  paginationConfig: { value: number; label: number }[];
  onPaginationChange: (event: any) => void;
}

export function SearchResults({
  results,
  selectedIds,
  remainingTaxIds,
  taxIdLimit,
  rangeStart,
  rangeEnd,
  totalCount,
  onToggle,
  onDeselectAll,
  hasSelections,
  selectedCount,
  onClose,
  onApply,
  tableData,
  paginationConfig,
  onPaginationChange,
}: SearchResultsProps) {
  const totalTaxIdCount = results.reduce((total, contract) => total + contract.taxIdCount, 0);
  const selectableContracts = results.filter(
    (contract) => selectedIds.has(contract.contractId) || contract.taxIdCount <= remainingTaxIds
  );
  const areAllSelectableContractsSelected =
    selectableContracts.length > 0 && selectableContracts.every((contract) => selectedIds.has(contract.contractId));
  const isOverLimit = totalTaxIdCount > taxIdLimit;
  const hasSelectedContracts = selectedIds.size > 0;


  return (
    <SearchResultsStyle aria-label="Contract ID search results" role="listbox">
      <ResultsHeaderStyle>
        <ResultsCountLabelStyle>
          Showing {rangeStart}-{rangeEnd} of {totalCount}
        </ResultsCountLabelStyle>
      </ResultsHeaderStyle>
      <SelectAllRowStyle>
        <CheckboxStyle
          type="checkbox"
          checked={isOverLimit ? hasSelectedContracts : areAllSelectableContractsSelected}
          disabled={isOverLimit ? !hasSelectedContracts : selectableContracts.length === 0}
          aria-label={isOverLimit || areAllSelectableContractsSelected ? 'Uncheck All' : 'Select All'}
          onChange={() => {
            if ((isOverLimit && hasSelectedContracts) || areAllSelectableContractsSelected) {
              onDeselectAll();
            } else {
              selectableContracts
                .filter((contract) => !selectedIds.has(contract.contractId))
                .forEach((contract) => onToggle(contract.contractId));
            }
          }}
        />
        <SelectAllTextStyle>Select All / Uncheck All</SelectAllTextStyle>
      </SelectAllRowStyle>
      <ResultsListStyle>
        {Array.from({ length: Math.ceil(results.length / 5) }, (_, rowIndex) => (
          <ResultsRowStyle key={`contract-row-${rowIndex}`}>
            {results.slice(rowIndex * 5, rowIndex * 5 + 5).map((contract) => {
              const isSelected = selectedIds.has(contract.contractId);
              const isDisabled = !isSelected && contract.taxIdCount > remainingTaxIds;
              return (
                <ContractIdResultItem
                  key={contract.contractId}
                  contract={{ ...contract, isSelected, isDisabled }}
                  onToggle={onToggle}
                />
              );
            })}
          </ResultsRowStyle>
        ))}
      </ResultsListStyle>
      <ModalFooter
        hasSelections={hasSelections}
        selectedCount={selectedCount}
        onClose={onClose}
        onApply={onApply}
        tableData={tableData}
        paginationConfig={paginationConfig}
        onPaginationChange={onPaginationChange}
      />
    </SearchResultsStyle>
  );
}
